import React, { useEffect, useState } from "react";
import { FaDownload, FaSpinner } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { useBoletin } from "../../context/BoletinContext";

const BoletinListStudentView = ({ boletines }) => {
  const { user } = useAuth();
  const { verificarAcceso, verificarAccesoAPeriodo } = useBoletin();
  
  const [tieneAcceso, setTieneAcceso] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [accesosPorPeriodo, setAccesosPorPeriodo] = useState({}); // 🔥 Nuevo estado
  const [verificacionCompleta, setVerificacionCompleta] = useState(false); // 🔥 Flag para saber si terminó

  // Verificar acceso del estudiante al montar el componente
  useEffect(() => {
    const verificarAccesoEstudiante = async () => {
      if (!user?.id) return;
      
      setCargando(true);
      setVerificacionCompleta(false);
      
      try {
        const accesoGeneral = await verificarAcceso(user.id);
        setTieneAcceso(accesoGeneral);

        if (accesoGeneral && boletines.length > 0) {
          const boletinesDelEstudiante = boletines.filter(
            (boletin) => boletin.students_id === user.id
          );

          const accesos = {};
          
          for (const boletin of boletinesDelEstudiante) {
            const tieneAccesoPeriodo = await verificarAccesoAPeriodo(
              user.id,
              boletin.periodo_id
            );
            accesos[boletin.periodo_id] = tieneAccesoPeriodo;
          }
          
          setAccesosPorPeriodo(accesos);
        } else {
          return;
        }
        
        setVerificacionCompleta(true);
      } catch (error) {
        setTieneAcceso(false);
        setVerificacionCompleta(true);
      } finally {
        setCargando(false);
      }
    };
    verificarAccesoEstudiante();
  }, [user?.id, verificarAcceso, verificarAccesoAPeriodo, boletines]);

  // Mostrar loader mientras se verifica el acceso
  if (cargando || tieneAcceso === null || !verificacionCompleta) {
    return (
      <div className="bg-white p-8 rounded-2xl shadow-md mt-10 flex flex-col items-center justify-center">
        <FaSpinner className="animate-spin text-4xl text-blue-600 mb-4" />
        <p className="text-gray-600">Verificando acceso a boletines...</p>
      </div>
    );
  }

  // Mostrar mensaje si el estudiante no tiene acceso
  if (tieneAcceso === false) {
    return (
      <div className="bg-white p-8 rounded-2xl shadow-md mt-10 flex flex-col items-center text-center">
        <img
          src="/logo.png"
          alt="Logo Colegio"
          className="w-64 mb-4 drop-shadow-xl"
        />
        <p className="text-xl font-semibold max-w-md text-gray-700">
          Los boletines no están disponibles actualmente. Por favor, comunícate
          con la institución si crees que esto es un error.
        </p>
      </div>
    );
  }

  // Filtrar boletines del estudiante actual
  const boletinesDelEstudiante = boletines.filter(
    (boletin) => boletin.students_id === user.id
  );

  // 🔥 Filtrar boletines según el acceso por periodo
  const boletinesAccesibles = boletinesDelEstudiante.filter((boletin) => {
    // Si no hay verificación para este periodo, asumir que SÍ tiene acceso
    const accesoPeriodo = accesosPorPeriodo[boletin.periodo_id];
    return accesoPeriodo !== false; // Solo ocultar si explícitamente es false
  });

  const handleDownloadPDF = async (pdfUrl) => {
    try {
      const fileResponse = await fetch(pdfUrl);
      const blob = await fileResponse.blob();
      const url = window.URL.createObjectURL(blob);

      const decodedUrl = decodeURIComponent(pdfUrl);
      const parts = decodedUrl.split("/");
      let rawFilename = parts[parts.length - 1];
      if (!rawFilename.endsWith(".pdf")) rawFilename += ".pdf";

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", rawFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("Error al descargar el boletín. Por favor, intenta nuevamente.");
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl shadow-md mt-6">
      <h3 className="text-xl font-semibold mb-4">Mis Boletines</h3>
      
      {boletinesDelEstudiante.length === 0 ? (
        <p className="text-gray-600 text-center py-4">
          No tienes boletines registrados.
        </p>
      ) : boletinesAccesibles.length === 0 ? (
        <div className="text-center py-8">
          <img
            src="/logo.png"
            alt="Logo Colegio"
            className="w-48 mx-auto mb-4 drop-shadow-xl"
          />
          <p className="text-lg font-semibold text-gray-700">
            Los boletines de los períodos seleccionados no están disponibles actualmente.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Por favor, comunícate con la institución si crees que esto es un error.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table-auto w-full border-collapse border border-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2">Periodo</th>
                <th className="border px-4 py-2">Inasistencias</th>
                <th className="border px-4 py-2">Promedio</th>
                <th className="border px-4 py-2">Acción</th>
              </tr>
            </thead>
            <tbody>
              {boletinesAccesibles.map((boletin) => (
                <tr key={`${boletin.students_id}-${boletin.periodo_id}`}>
                  <td className="border px-4 py-2 text-center">
                    {boletin.periodo_nombre}
                  </td>
                  <td className="border px-4 py-2 text-center">
                    {boletin.inasistencias}
                  </td>
                  <td className="border px-4 py-2 text-center">
                    {boletin.promedio_final_periodo?.toFixed(2) || "-"}
                  </td>
                  <td className="border px-4 py-2 text-center">
                    {boletin.pdf_url ? (
                      <button
                        onClick={() => handleDownloadPDF(boletin.pdf_url)}
                        className="text-green hover:text-main-blue transition-colors"
                        title="Descargar PDF"
                      >
                        <FaDownload size={20} />
                      </button>
                    ) : (
                      <FaDownload
                        size={20}
                        className="text-gray-400 cursor-not-allowed mx-auto"
                        title="PDF no disponible"
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* 🔥 Mensaje informativo si hay boletines ocultos */}
          {boletinesDelEstudiante.length > boletinesAccesibles.length && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 text-center">
                ℹ️ Algunos boletines no están disponibles actualmente debido a restricciones administrativas.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BoletinListStudentView;